import type { AdvisorDraftItem } from '../model/types';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import css from '@itineraries/widgets/planner-products-pane/ui/source-product-card.module.scss';
import { formatPrice } from '@products/entities/product/lib/price';
import { resolveThumbnailUrl } from '@travel-planner/shared/utils';
import { ImageOff, X } from 'lucide-react';
import { getCategoryIcon } from '@/shared/lib/category-icon';
import { Button } from '@/shared/ui/button';
import { Text } from '@/shared/ui/text';
import { advisorItemDragId } from '../model/dnd';
import layout from './advisor-planner-workspace.module.scss';

const KIND_CATEGORY: Record<AdvisorDraftItem['kind'], string> = {
  hotel: 'Accommodation',
  restaurant: 'Dining',
  experience: 'Activity',
};

type AdvisorDayItemProps = {
  item: AdvisorDraftItem;
  onRemove: () => void;
};

export const AdvisorDayItem = ({ item, onRemove }: AdvisorDayItemProps) => {
  const sortable = useSortable({
    id: advisorItemDragId(item.id),
    data: { kind: 'advisor-item', itemId: item.id },
  });
  const category = KIND_CATEGORY[item.kind];
  const CategoryIcon = getCategoryIcon(category);
  const price = formatPrice(item.clientPrice, item.currency);
  const thumb = resolveThumbnailUrl(item.thumbnail);
  return (
    <div
      className={layout.dayItemRow}
      ref={sortable.setNodeRef}
      style={{
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
        zIndex: sortable.isDragging ? 2 : undefined,
      }}
    >
      <div
        className={css.root}
        {...sortable.attributes}
        {...sortable.listeners}
      >
        <span className={css.thumb}>
          {thumb ? (
            <img alt="" className={css.thumbImg} src={thumb} />
          ) : (
            <ImageOff
              aria-hidden="true"
              className={css.thumbPlaceholder}
              size={20}
            />
          )}
        </span>
        <span className={css.body}>
          <span className={css.titleRow}>
            <CategoryIcon
              aria-label={category}
              className={css.categoryIcon}
              size={14}
            />
            <Text as="span" className={css.title} size="sm" weight="medium">
              {item.title}
            </Text>
          </span>
          <span className={css.metaRow}>
            {item.origin === 'suggested' ? (
              <span className={css.metaItem}>Suggested</span>
            ) : null}
            {item.subtitle && (
              <span className={css.metaItem}>{item.subtitle}</span>
            )}
            <span className={css.metaItem}>{price}</span>
          </span>
        </span>
      </div>
      <Button
        aria-label={`Remove ${item.title}`}
        onClick={onRemove}
        size="iconSm"
        type="button"
        variant="ghost"
      >
        <X size={14} />
      </Button>
    </div>
  );
};
